import _ from 'lodash';
import moment from 'moment-timezone';
import { sqCapsuleRuntimeStore, sqTrendCapsuleStore, sqTrendSeriesStore, sqTrendStore } from '@/core/core.stores';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { getSeriesIdsByCalculationType } from '@/trend/trendDataHelper.utilities';
import { mergeCapsuleSelection } from '@/trendData/trendCapsule.utilities';
import { ITEM_CHILDREN_TYPES } from '@/trendData/trendData.constants';
import { getAlternateEndTime } from '@/trendData/trendSeries.utilities';
import type { SeriesStoreItem, TrendCapsuleStoreItem } from '@/trendData/trendSeries.store';
import type { CapsuleSeriesFields } from '@/trendData/trendSeries.utilities';

/**
 * Selectors that combine the series store with the capsule and trend stores. They live outside the
 * stores so no store has to read another store's state.
 */

/**
 * Returns all series that are converted from a capsule.
 *
 * Use the `useCapsuleSeries` hook in components to minimize re-renders.
 */
export function getCapsuleSeries(): SeriesStoreItem[] {
  const hideUnselectedItems = Boolean(sqTrendStore.hideUnselectedItems);
  const referenceInterestIds = getSeriesIdsByCalculationType(TREND_TOOLS.REFERENCE, sqTrendSeriesStore) ?? [];

  let capsuleStoreItems: TrendCapsuleStoreItem[] = mergeCapsuleSelection(
    sqCapsuleRuntimeStore.getState().items,
    sqTrendCapsuleStore.selectedCapsules,
  ) as TrendCapsuleStoreItem[];
  const someCapsulesSelected = capsuleStoreItems.some((capsule) => capsule.selected);
  const longestCapsuleFromStore = _.maxBy(capsuleStoreItems, (capsule) => capsule.duration ?? 0);

  if (someCapsulesSelected) {
    capsuleStoreItems = capsuleStoreItems.filter((capsule) => capsule.selected);

    // When we aren't dimming, we want to make sure to keep the longest overall capsule so that when we filter out
    // the reference profiles, we show the longest slightly transparent and the selected one with full opacity
    if (!hideUnselectedItems && longestCapsuleFromStore) {
      capsuleStoreItems.push(longestCapsuleFromStore);
    }
  }

  const capsuleIds = capsuleStoreItems.map((capsule) => capsule.id);

  const [referenceCapsuleSeries, nonReferenceCapsuleSeries] = _.chain(sqTrendSeriesStore.seriesItems)
    .filter((item) => item.isChildOf && item.childType === ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE)
    .uniqBy('id')
    .partition((item) => referenceInterestIds.includes(item.isChildOf as string))
    .value() as [SeriesStoreItem[], SeriesStoreItem[]];

  const filteredAndGroupedReferenceCapsuleSeries = _.chain(referenceCapsuleSeries)
    .filter((item) => item.capsuleId && capsuleIds.includes(item.capsuleId))
    .groupBy((item) => item.idChildOf ?? '')
    .map((group) => _.groupBy(group, 'id'))
    .value() as Array<Record<string, SeriesStoreItem[]>>;

  const referenceCapsuleSeriesToKeep: SeriesStoreItem[] = [];

  filteredAndGroupedReferenceCapsuleSeries.forEach((referenceGroup) => {
    Object.values(referenceGroup).forEach((capsuleGroup) => {
      if (
        someCapsulesSelected &&
        !hideUnselectedItems &&
        longestCapsuleFromStore &&
        !longestCapsuleFromStore.selected
      ) {
        const longestOverallIndex = capsuleGroup.findIndex((series) => series.capsuleId === longestCapsuleFromStore.id);
        if (longestOverallIndex >= 0) {
          const [longestOverall] = capsuleGroup.splice(longestOverallIndex, 1);
          if (longestOverall) {
            referenceCapsuleSeriesToKeep.push(longestOverall);
          }
        }
      }

      const maxSeries = _.maxBy(
        capsuleGroup,
        (series) => series.duration ?? (series.endTime ?? 0) - (series.startTime ?? 0),
      );
      if (maxSeries) {
        referenceCapsuleSeriesToKeep.push(maxSeries);
      }
    });
  });

  return [...nonReferenceCapsuleSeries, ...referenceCapsuleSeriesToKeep];
}

/**
 * Returns every series of the capsule, including those `getCapsuleSeries` hides for the current selection.
 */
export function getSeriesOfCapsule(capsuleId: string): Array<SeriesStoreItem & CapsuleSeriesFields> {
  return sqTrendSeriesStore.seriesItems.filter(
    (item): item is SeriesStoreItem & CapsuleSeriesFields =>
      item.childType === ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE && item.capsuleId === capsuleId,
  );
}

/**
 * Gets the longest capsule series duration
 *
 * @return The longest capsule series duration or zero if there are no capsule series.
 */
export function getLongestCapsuleSeriesDuration(): number {
  // Be sure to include in progress capsules
  let longestInProgress = 0;
  const inProgressCapsuleSeries = getCapsuleSeries().filter((series) => series.duration == null);
  if (inProgressCapsuleSeries.length > 0) {
    const earliestStart = _.min(
      inProgressCapsuleSeries
        .map((series) => series.startTime)
        .filter((start): start is number => typeof start === 'number'),
    );
    if (typeof earliestStart === 'number') {
      longestInProgress = moment.duration(getAlternateEndTime() - earliestStart).asMilliseconds();
    }
  }
  const longestDuration = _.maxBy(getCapsuleSeries(), (series) => series.duration ?? 0)?.duration ?? 0;
  return Math.max(longestInProgress, longestDuration);
}
